import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, ArrowRight, Ticket, Globe2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function EventiList() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/eventi`)
      .then((r) => {
        setUpcoming(r.data.upcoming || []);
        setPast(r.data.past || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: upcoming.slice(0, 10).map((e, i) => ({
      "@type": "Event",
      position: i + 1,
      name: e.titolo,
      startDate: e.data_inizio,
      endDate: e.data_fine || undefined,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode:
        e.modalita === "online"
          ? "https://schema.org/OnlineEventAttendanceMode"
          : "https://schema.org/OfflineEventAttendanceMode",
      location:
        e.modalita === "online"
          ? { "@type": "VirtualLocation", url: `https://assoacdt.com/eventi/${e.slug}` }
          : { "@type": "Place", name: e.luogo, address: e.indirizzo || undefined },
      offers:
        e.prezzo_eur > 0
          ? {
              "@type": "Offer",
              price: e.prezzo_eur,
              priceCurrency: "EUR",
              url: `https://assoacdt.com/eventi/${e.slug}`,
            }
          : undefined,
    })),
  };

  return (
    <div className="relative min-h-screen bg-white">
      <SEO
        title="Eventi & Webinar A.C.D.T. — Formazione Consulenti Digitali"
        description="Calendario eventi A.C.D.T.: webinar gratuiti, workshop pratici e formazione continua per Consulenti Digitali certificati. Iscrizioni online con pagamento sicuro."
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="relative pt-16 md:pt-20 pb-10 md:pb-14 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#f59e0b]/20 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b]/20 text-[#fbbf24] text-[11px] font-extrabold uppercase tracking-[0.2em] mb-4">
            <Ticket className="h-3.5 w-3.5" /> Calendario eventi
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl">
            Eventi & Webinar A.C.D.T.
          </h1>
          <p className="mt-4 text-slate-200 text-base md:text-lg max-w-2xl">
            Webinar gratuiti, workshop pratici e sessioni di formazione continua per
            Consulenti Digitali certificati. Iscrizione online sicura con Stripe.
          </p>
        </div>
      </section>

      <section className="relative py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex items-baseline justify-between mb-8">
            <div>
              <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
                Prossimi eventi
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-extrabold text-[#0a1f55]">
                In arrivo
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="text-center text-slate-500 py-14">Caricamento eventi…</div>
          ) : upcoming.length === 0 ? (
            <div
              data-testid="eventi-empty"
              className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500"
            >
              Nessun evento in programma. Torna presto per il prossimo appuntamento!
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="eventi-upcoming">
              {upcoming.map((e) => (
                <EventoCard key={e.id} evento={e} />
              ))}
            </div>
          )}

          {past.length > 0 && (
            <>
              <div className="mt-16 mb-8">
                <div className="text-xs font-bold tracking-[0.22em] uppercase text-slate-400">
                  Eventi passati
                </div>
                <h3 className="mt-2 text-xl md:text-2xl font-extrabold text-slate-600">
                  Già svolti
                </h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4" data-testid="eventi-past">
                {past.map((e) => (
                  <EventoCard key={e.id} evento={e} compact />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function EventoCard({ evento, compact = false }) {
  const isOnline = evento.modalita === "online";
  const gratis = !evento.prezzo_eur || evento.prezzo_eur <= 0;
  return (
    <Link
      to={`/eventi/${evento.slug}`}
      data-testid={`evento-card-${evento.slug}`}
      className={`group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all ${
        compact ? "opacity-90 hover:opacity-100" : ""
      }`}
    >
      <div className="aspect-[16/9] bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] relative overflow-hidden">
        {evento.cover_url ? (
          <img
            src={evento.cover_url}
            alt={evento.titolo}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-5xl font-black">
            A.C.D.T.
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          {isOnline && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500 text-white text-[10.5px] font-extrabold uppercase tracking-widest">
              <Globe2 className="h-3 w-3" /> Online
            </span>
          )}
          {gratis && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10.5px] font-extrabold uppercase tracking-widest">
              Gratuito
            </span>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-widest">
            {gratis ? "Iscriviti" : `€ ${evento.prezzo_eur}`}
          </span>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className={`font-extrabold text-[#0a1f55] leading-snug ${compact ? "text-base" : "text-lg"}`}>
          {evento.titolo}
        </h3>
        {evento.sottotitolo && !compact && (
          <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed line-clamp-2">
            {evento.sottotitolo}
          </p>
        )}
        <div className="mt-3 space-y-1.5 text-[13px] text-slate-600">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#f59e0b]" />
            <span className="font-semibold">
              {formatDate(evento.data_inizio)} · {formatTime(evento.data_inizio)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#f59e0b]" />
            <span>{evento.luogo || "—"}</span>
          </div>
          {evento.capienza_max > 0 && evento.posti_disponibili !== undefined && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[#f59e0b]" />
              <span>
                {evento.posti_disponibili !== null
                  ? `${evento.posti_disponibili} posti disponibili`
                  : "Posti illimitati"}
              </span>
            </div>
          )}
        </div>
        <div className="mt-4 inline-flex items-center gap-1.5 text-[#f59e0b] text-sm font-bold group-hover:gap-2 transition-all">
          Dettagli <ArrowRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}
